import classNames from 'classnames';

import { getReferenceCardProps } from '@/src/lib/getReferenceCardProps';
import type { DataAttributes, Intro as IntroType, ReferenceCardsBlock } from '@/types';
import { Intro } from '@/ui/Intro/Intro';
import { Section } from '@/ui/Section/Section';

import { Card } from './components/Card/Card';
import { ExampleCard } from './components/ExampleCard/ExampleCard';
import { RoundtableCard } from './components/RoundtableCard/RoundtableCard';

import styles from './ReferenceCards.module.scss';

interface Props extends DataAttributes {
    cards?: ReferenceCardsBlock['cards'];
    className?: string;
    collectionClassName?: string;
    itemsClassName?: string;
    hasIntro?: boolean;
    intro?: IntroType;
    introNextToCards?: boolean;
    backgroundColor?: 'white' | 'gray';
    isHero?: boolean;
}

export function ReferenceCards({
    backgroundColor = 'white',
    className,
    collectionClassName,
    itemsClassName,
    cards,
    isHero,
    hasIntro,
    intro,
    introNextToCards,
    ...attributes
}: Props) {
    return (
        <Section
            data-reference-cards
            data-reference-cards-count={cards?.length ?? 0}
            data-reference-cards-is-hero={isHero}
            {...attributes}
            className={className}
            primary={backgroundColor}
            isHero={isHero}
        >
            <div
                className={classNames(styles.collection, collectionClassName, {
                    [styles.introNextToCards]: introNextToCards,
                })}
            >
                {hasIntro && intro && (
                    <Intro
                        {...intro}
                        className={styles.intro}
                        classes={{ title: styles.title, body: styles.body, byline: styles.byline }}
                        isHero={isHero}
                        hasBackground={backgroundColor !== 'white'}
                    />
                )}

                {cards && cards.length > 0 && (
                    <div
                        className={classNames(styles.items, itemsClassName, {
                            [styles.hasIntro]: hasIntro,
                        })}
                    >
                        {cards.map(({ card, hideReadMoreLabel, id }) => {
                            if (!card) {
                                return null;
                            }

                            const props = getReferenceCardProps(card, { hideReadMoreLabel });

                            if (card.contentType === 'examples') {
                                return (
                                    <ExampleCard
                                        key={id}
                                        example={card}
                                        slug={card.slug}
                                        {...props}
                                    />
                                );
                            }

                            if (card.contentType === 'round-tables') {
                                return <RoundtableCard key={id} video={card.video} {...props} />;
                            }

                            return <Card key={id} {...props} />;
                        })}
                    </div>
                )}
            </div>
        </Section>
    );
}
